import {
  Badge,
  Button,
  Card,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  CheckmarkCircle16Filled,
  ChevronDownRegular,
  ChevronRightRegular,
} from '@fluentui/react-icons';

import type {
  AnnotationSet,
  DataEntry,
  DataFile,
} from '../../../../../../services/dataHubService.interface.js';
import { canLoadDataFilePreview } from '../../utils/detectDataFileParser.js';

import { useStyles } from './AnnotationSourceItem.style.js';

const ANNOTATED_LABEL = 'Annotated';
const ANNOTATED_TOOLTIP = 'This file has saved annotation documents';
const DRAFT_LABEL = 'Draft saved';
const PUBLISHED_LABEL = 'Published';

export interface AnnotationSourceItemProps {
  entry: DataEntry;
  files: readonly DataFile[];
  expanded: boolean;
  isLoading: boolean;
  error?: string | null;
  disabled?: boolean;
  selectedFileId?: string;
  annotatedFileIds?: ReadonlySet<string>;
  fileStates?: ReadonlyMap<string, ReadonlySet<AnnotationSet['status']>>;
  onToggle: (dataId: string) => void;
  onSelectFile: (dataId: string, file: DataFile) => void;
}

export function AnnotationSourceItem({
  entry,
  files,
  expanded,
  isLoading,
  error,
  disabled,
  selectedFileId,
  annotatedFileIds,
  fileStates,
  onToggle,
  onSelectFile,
}: AnnotationSourceItemProps) {
  const styles = useStyles();
  const supportedFiles = files.filter(canLoadDataFilePreview);
  return (
    <>
      <Button
        appearance="subtle"
        className={styles.entry}
        disabled={disabled}
        aria-expanded={expanded}
        onClick={() => onToggle(entry.dataId)}
        icon={expanded ? <ChevronDownRegular /> : <ChevronRightRegular />}
      >
        <Text
          truncate
          wrap={false}
          className={styles.name}
          title={entry.name ?? entry.dataId}
        >
          {entry.name ?? entry.dataId}
        </Text>
        {!expanded &&
          supportedFiles.some((file) => annotatedFileIds?.has(file.id)) && (
            <Tooltip content={ANNOTATED_TOOLTIP} relationship="label">
              <CheckmarkCircle16Filled
                aria-label={ANNOTATED_LABEL}
                className={styles.annotatedIcon}
              />
            </Tooltip>
          )}
      </Button>
      {expanded && (
        <Card className={styles.files}>
          {isLoading ? (
            <Spinner size="tiny" label="Loading files…" />
          ) : error ? (
            <MessageBar intent="error" role="alert">
              <MessageBarBody>{error}</MessageBarBody>
            </MessageBar>
          ) : supportedFiles.length === 0 ? (
            <Text>No annotatable files in this entry.</Text>
          ) : (
            supportedFiles.map((file) => (
              <Button
                key={file.id}
                appearance="subtle"
                disabled={disabled}
                aria-pressed={file.id === selectedFileId}
                className={mergeClasses(
                  styles.file,
                  file.id === selectedFileId && styles.selected,
                )}
                onClick={() => onSelectFile(entry.dataId, file)}
              >
                <Text
                  truncate
                  wrap={false}
                  className={styles.name}
                  title={file.fileName ?? file.id}
                >
                  {file.fileName ?? file.id}
                </Text>
                {annotatedFileIds?.has(file.id) && (
                  <Tooltip content={ANNOTATED_TOOLTIP} relationship="label">
                    <Badge
                      color="success"
                      appearance="tint"
                      size="small"
                      aria-label={ANNOTATED_LABEL}
                    >
                      {fileStates?.get(file.id)?.has('Draft')
                        ? fileStates.get(file.id)?.has('Published')
                          ? `${DRAFT_LABEL} / ${PUBLISHED_LABEL}`
                          : DRAFT_LABEL
                        : PUBLISHED_LABEL}
                    </Badge>
                  </Tooltip>
                )}
              </Button>
            ))
          )}
        </Card>
      )}
    </>
  );
}
